import LegalLayout from "@/components/LegalLayout";
import { Mail, MessageCircle, Twitter, Instagram, Youtube } from "lucide-react";

function H2({ children }) {
  return (
    <h2 className="mt-10 font-heading text-2xl md:text-3xl"
      style={{ fontVariationSettings: "'opsz' 64, 'wght' 500", color: "var(--ink)" }}>{children}</h2>
  );
}

function P({ children }) { return <p>{children}</p>; }

const CHANNELS = [
  {
    icon: Mail,
    label: "Email",
    value: "hello@pintaflow.com",
    href: "mailto:hello@pintaflow.com",
    note: "Best for anything — refunds, bugs, feature requests, or a hello.",
    testid: "contact-email",
  },
  {
    icon: MessageCircle,
    label: "In-app feedback",
    value: "Tap the Feedback button (bottom-right)",
    href: null,
    note: "Fastest way to reach us — attaches the page you're on automatically.",
    testid: "contact-feedback",
  },
  {
    icon: Twitter,
    label: "Twitter / X",
    value: "@pradeepmunjal",
    href: "https://twitter.com/pradeepmunjal",
    note: "DMs open. Public replies too — great for feature discussions.",
    testid: "contact-twitter",
  },
];

export default function Contact() {
  return (
    <LegalLayout
      eyebrow="iv. Say hello"
      title="Contact"
      italic="us."
      lastUpdated="July 17, 2026"
      testid="contact-page"
    >
      <P>
        PintaFlow is a small, hands-on operation — a single founder (<strong style={{ color: "var(--ink)" }}>Pradeep Munjal</strong>) building for creators he genuinely respects. So when you email us, you reach us. No ticket queues, no macros.
      </P>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8" data-testid="contact-channels">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          const inner = (
            <div
              data-testid={c.testid}
              className="rounded-2xl p-5 h-full flex flex-col gap-2.5 transition-colors"
              style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: "rgba(216,168,87,0.10)", color: "var(--seal)" }}
              >
                <Icon size={16} strokeWidth={1.6} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--seal)" }}>
                {c.label}
              </div>
              <div className="text-sm" style={{ color: "var(--ink)", fontVariationSettings: "'wght' 500" }}>
                {c.value}
              </div>
              <div className="text-[13px]" style={{ color: "var(--ink-72)" }}>
                {c.note}
              </div>
            </div>
          );
          return c.href ? (
            <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
              {inner}
            </a>
          ) : (
            <div key={c.label}>{inner}</div>
          );
        })}
      </div>

      <H2>Response times</H2>
      <P>
        We reply within <strong style={{ color: "var(--ink)" }}>24 hours on business days</strong> (Mon-Fri, IST). Weekends may be slower, but we always read.
      </P>

      <H2>What to include</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Your account email (the one you signed up with).</li>
        <li>A one-line description of what you need — bug, refund, feature, general question.</li>
        <li>Screenshots or the page URL, if it&apos;s a visual issue.</li>
      </ul>

      <H2>Business address</H2>
      <P>
        Pintaflow is a project by Pradeep Munjal, based in <strong style={{ color: "var(--ink)" }}>Delhi, India</strong>. For legal correspondence, please email first — we will share our postal address if needed.
      </P>

      <H2>Not a fit?</H2>
      <P>
        If PintaFlow isn&apos;t the right tool for you, tell us why. Genuinely — those emails often shape the roadmap more than the positive ones. And of course, our <a href="/refund" style={{ color: "var(--seal)" }}>7-day refund</a> has your back on any paid plan.
      </P>
    </LegalLayout>
  );
}
